/**
 * Computes the midpoint of the VISIBLE portion of an SVG path within the viewport.
 * 
 * Used by edge insert (+) buttons so they always appear:
 * 1. On the actual rendered path (not floating in space on curved/routed edges)
 * 2. Within the visible viewport (so the button is always reachable on long edges)
 */

const NUM_SAMPLES = 60;

function parsePathCommands(pathD) {
  if (!pathD) return [];
  const commands = [];
  const regex = /([MLQ])\s*([-\d.]+(?:\s+[-\d.]+)*)/gi;
  let match;
  while ((match = regex.exec(pathD)) !== null) {
    const cmd = match[1].toUpperCase();
    const nums = match[2].trim().split(/\s+/).map(Number);
    if (cmd === 'M' || cmd === 'L') {
      commands.push({ type: cmd, x: nums[0], y: nums[1] });
    } else if (cmd === 'Q') {
      commands.push({ type: 'Q', cx: nums[0], cy: nums[1], x: nums[2], y: nums[3] });
    }
  }
  return commands;
}

function quadraticBezierPoint(p0, p1, p2, t) {
  const mt = 1 - t;
  return {
    x: mt * mt * p0.x + 2 * mt * t * p1.x + t * t * p2.x,
    y: mt * mt * p0.y + 2 * mt * t * p1.y + t * t * p2.y,
  };
}

function segmentLength(x1, y1, x2, y2) {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

function bezierLength(p0, p1, p2, steps = 10) {
  let len = 0;
  let prev = p0;
  for (let i = 1; i <= steps; i++) {
    const pt = quadraticBezierPoint(p0, p1, p2, i / steps);
    len += segmentLength(prev.x, prev.y, pt.x, pt.y);
    prev = pt;
  }
  return len;
}

/**
 * Sample evenly-spaced points along an SVG path string.
 * Returns canvas-coordinate points.
 */
function samplePath(pathD) {
  const commands = parsePathCommands(pathD);
  if (commands.length < 2) return [];

  const segs = [];
  let totalLen = 0;
  let cur = { x: 0, y: 0 };

  for (const cmd of commands) {
    if (cmd.type === 'M') {
      cur = { x: cmd.x, y: cmd.y };
    } else if (cmd.type === 'L') {
      const len = segmentLength(cur.x, cur.y, cmd.x, cmd.y);
      segs.push({ type: 'L', start: { ...cur }, end: { x: cmd.x, y: cmd.y }, length: len, startLen: totalLen });
      totalLen += len;
      cur = { x: cmd.x, y: cmd.y };
    } else if (cmd.type === 'Q') {
      const p0 = { ...cur };
      const p1 = { x: cmd.cx, y: cmd.cy };
      const p2 = { x: cmd.x, y: cmd.y };
      const len = bezierLength(p0, p1, p2);
      segs.push({ type: 'Q', p0, p1, p2, length: len, startLen: totalLen });
      totalLen += len;
      cur = { x: cmd.x, y: cmd.y };
    }
  }

  if (totalLen === 0) return [];

  const points = [];
  for (let i = 0; i <= NUM_SAMPLES; i++) {
    const target = (i / NUM_SAMPLES) * totalLen;
    for (const seg of segs) {
      if (target >= seg.startLen && target <= seg.startLen + seg.length) {
        const t = seg.length > 0 ? (target - seg.startLen) / seg.length : 0;
        let pt;
        if (seg.type === 'L') {
          pt = {
            x: seg.start.x + t * (seg.end.x - seg.start.x),
            y: seg.start.y + t * (seg.end.y - seg.start.y),
          };
        } else {
          pt = quadraticBezierPoint(seg.p0, seg.p1, seg.p2, t);
        }
        if (pt) points.push(pt);
        break;
      }
    }
  }
  return points;
}

/** Unit normal perpendicular to path direction at a sample index (screen-space consistent). */
function computePathNormal(points, index) {
  const prev = points[Math.max(0, index - 3)];
  const next = points[Math.min(points.length - 1, index + 3)];
  let tx = next.x - prev.x;
  let ty = next.y - prev.y;
  const len = Math.hypot(tx, ty);
  if (len < 1e-6) return { normalX: 1, normalY: 0 };
  tx /= len;
  ty /= len;
  // 90° clockwise from tangent — consistent side offset for any edge orientation
  return { normalX: ty, normalY: -tx };
}

function findVisibleMidpointIndex(points, pan, zoom, containerRect) {
  if (!containerRect || !pan || zoom == null) {
    return Math.floor(points.length / 2);
  }

  const viewLeft   = -pan.x / zoom;
  const viewTop    = -pan.y / zoom;
  const viewRight  = (containerRect.width - pan.x) / zoom;
  const viewBottom = (containerRect.height - pan.y) / zoom;

  const margin = 24 / zoom;
  const vl = viewLeft + margin;
  const vt = viewTop + margin;
  const vr = viewRight - margin;
  const vb = viewBottom - margin;

  const visibleIndices = points
    .map((p, i) => (p.x >= vl && p.x <= vr && p.y >= vt && p.y <= vb ? i : -1))
    .filter(i => i >= 0);

  if (visibleIndices.length > 0) {
    return visibleIndices[Math.floor(visibleIndices.length / 2)];
  }

  return Math.floor(points.length / 2);
}

/**
 * Given an SVG path string (in canvas coords), the current pan/zoom, and the
 * container rect, return the canvas-coordinate point that sits at the midpoint
 * of the path's visible portion.
 *
 * Falls back to the overall path midpoint when the viewport can't be determined
 * or the entire path is off-screen.
 *
 * @param {string} pathD        SVG path `d` attribute (canvas coordinates)
 * @param {{ x: number, y: number }} pan   Current pan offset
 * @param {number} zoom          Current zoom level
 * @param {{ width: number, height: number }} containerRect  Container dimensions
 * @returns {{ x: number, y: number } | null}
 */
export function getVisiblePathMidpoint(pathD, pan, zoom, containerRect) {
  const info = getVisiblePathMidpointWithNormal(pathD, pan, zoom, containerRect);
  return info ? { x: info.x, y: info.y } : null;
}

/**
 * Like getVisiblePathMidpoint but also returns a unit normal for offsetting UI
 * perpendicular to the edge (e.g. insert button on hover).
 *
 * @returns {{ x: number, y: number, normalX: number, normalY: number } | null}
 */
export function getVisiblePathMidpointWithNormal(pathD, pan, zoom, containerRect) {
  if (!pathD) return null;

  const points = samplePath(pathD);
  if (points.length === 0) return null;

  const midIndex = findVisibleMidpointIndex(points, pan, zoom, containerRect);
  const mid = points[midIndex];
  if (!mid) return null;

  const { normalX, normalY } = computePathNormal(points, midIndex);
  return { x: mid.x, y: mid.y, normalX, normalY };
}

/**
 * Simplified version that just returns the path midpoint (no viewport clipping).
 * Useful when viewport info isn't available.
 */
export function getPathMidpoint(pathD) {
  if (!pathD) return null;
  const points = samplePath(pathD);
  if (points.length === 0) return null;
  const mid = points[Math.floor(points.length / 2)];
  return mid ? { x: mid.x, y: mid.y } : null;
}
